import {
  createTheme,
  responsiveFontSizes,
  Theme,
  ThemeOptions,
  ComponentsOverrides,
  ComponentsProps,
  ComponentsVariants,
} from "@mui/material";

declare module "@mui/material/styles" {
  interface Components {
    MuiLoadingButton?: {
      defaultProps?: ComponentsProps["MuiButton"];
      styleOverrides?: ComponentsOverrides["MuiButton"];
      variants?: ComponentsVariants["MuiButton"];
    };
  }

  interface BreakpointOverrides {
    xxl: true;
    tablet: true;
  }

  interface Palette {
    themePrimary: {
      primary: string;
      secondary: string;
      tertiary: string;
      contrastText: string;
    };
    tertiary: Palette["primary"];
    ambientSayaColor: {
      main: string;
      secondary: string;
    };
    dataTableColor: {
      dataTableHeaderbg: string;
      dataTablepaginationbg: string;
    };
  }

  interface PaletteOptions {
    themePrimary?: {
      primary: string;
      secondary: string;
      tertiary: string;
      contrastText: string;
    };
    tertiary?: PaletteOptions["primary"];
    ambientSayaColor?: {
      main: string;
      secondary: string;
    };
    dataTableColor?: {
      dataTableHeaderbg: string;
      dataTablepaginationbg: string;
    };
  }

  interface CommonColors {
    lightBlack: string;
    grey: string;
    lightGrey: string;
    lightGreyTwo: string;
    secondaryTextGrey: string;
    middleLightGrey: string;
    blue: string;
    lightBlue: string;
    green: string;
    red: string;
    dark: string;
    softRed: string;
    lightRed: string;
    extraLightRed: string;
    extraDarkRed: string;
    purple: string;
    softBlue: string;
    borderColor: string;
  }

  interface TypeBackground {
    disclaimer: string;
  }

  interface TypographyVariants {
    modalLabel: React.CSSProperties;
    formLabel: React.CSSProperties;
    propertyLabel: React.CSSProperties;
    bodyL: React.CSSProperties;
    bodyM: React.CSSProperties;
    bodyS: React.CSSProperties;
    bodyXs: React.CSSProperties;
    body: React.CSSProperties;
    bodyBold: React.CSSProperties;
    input: React.CSSProperties;
    headline: React.CSSProperties;
    recommendationCaption: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    modalLabel?: React.CSSProperties;
    formLabel?: React.CSSProperties;
    propertyLabel?: React.CSSProperties;
    bodyL?: React.CSSProperties;
    bodyM?: React.CSSProperties;
    bodyS?: React.CSSProperties;
    bodyXs?: React.CSSProperties;
    body?: React.CSSProperties;
    bodyBold?: React.CSSProperties;
    input?: React.CSSProperties;
    headline?: React.CSSProperties;
    recommendationCaption?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    modalLabel: true;
    formLabel: true;
    propertyLabel: true;
    bodyL: true;
    bodyM: true;
    bodyS: true;
    bodyXs: true;
    body: true;
    bodyBold: true;
    input: true;
    headline: true;
    recommendationCaption: true;
  }
}

export const defaultPalette = {
  background: {
    default: "#F8F7FE",
    paper: "#fff",
    disclaimer: "#E6EFFF",
  },
  common: {
    black: "#222222",
    lightBlack: "#555555",
    white: "#fff",
    grey: "#A1A0A0",
    lightGrey: "#F5F5F5",
    lightGreyTwo: "#F0F0F0",
    secondaryTextGrey: "#666666",
    middleLightGrey: "#D1D1D1",
    blue: "#246EFD",
    lightBlue: "#E4EDFF",
    green: "#5ebc4e",
    red: "#fc5a5a",
    dark: "#272a40",
    softRed: "#ff5e5e",
    lightRed: "#FEEBEE",
    extraLightRed: "#FDEDED",
    extraDarkRed: "#5F2120",
    purple: "#711aff",
    softBlue: "#E5EBFF",
    borderColor: "#e0e0e0",
    // Onboarding signal. Deliberately its own colour, taken from the magenta in the
    // Nephrolytics logo mark: purple already means AI-authored, blue means the app,
    // green means provider-authored and red/orange mean clinical severity, so an
    // onboarding cue in any of those would be a misread. Magenta only ever means
    // "the product is teaching you something", and every future onboarding reuses it.
    onboarding: "#C2278C",
    // Tint behind the icon the onboarding cue is pointing at, and the marked-row fill.
    // Both taken verbatim from the design prototype (ONB_LT and rowBg).
    onboardingTint: "#FCEEF6",
    onboardingRow: "#F7F2FF",
  },
  mode: "light" as const,
  themePrimary: {
    primary: "#2D3748",
    secondary: "#555555",
    tertiary: "#315fff",
    contrastText: "#fff",
  },
  primary: {
    main: "#315fff",
    light: "#6573ff",
    dark: "#0039cb",
    contrastText: "#fff",
  },
  // TODO: update these colors once we have secondary colors
  secondary: {
    main: "#f50057",
    light: "#f73378",
    dark: "#ab003c",
    contrastText: "#fff",
  },
  tertiary: {
    main: "#333",
    light: "#555",
    dark: "#111",
    contrastText: "#fff",
  },
  ambientSayaColor: {
    main: "#A36AFF",
    secondary: "#7119FF",
  },
  dataTableColor: {
    dataTableHeaderbg: "#EFF4FB",
    dataTablepaginationbg: "#EBEBEB",
  },
  error: {
    main: "#d32f2f",
    light: "#ef5350",
    dark: "#c62828",
    contrastText: "#fff",
  },
  warning: {
    main: "#ED6C02",
    light: "rgb(255, 200, 117)",
    dark: "rgb(178, 122, 0)",
    contrastText: "rgba(0, 0, 0, 0.87)",
  },
  info: {
    main: "#246EFD",
    light: "rgb(71, 145, 219)",
    dark: "rgb(17, 82, 147)",
    contrastText: "#fff",
  },
  success: {
    main: "#2E7D32",
    light: "rgb(111, 207, 151)",
    dark: "rgb(53, 122, 63)",
    contrastText: "rgba(0, 0, 0, 0.87)",
  },
  text: {
    primary: "#2D3748",
    secondary: "#878787",
    disabled: "rgba(0, 0, 0, 0.38)",
    hint: "rgba(0, 0, 0, 0.23)",
  },
  action: {
    active: "rgba(0, 0, 0, 0.54)",
    hover: "rgba(0, 0, 0, 0.08)",
    hoverOpacity: 0.08,
    selected: "rgba(0, 0, 0, 0.14)",
    disabled: "rgba(0, 0, 0, 0.26)",
    disabledBackground: "rgba(0, 0, 0, 0.12)",
    disabledOpacity: 0.38,
    focus: "rgba(0, 0, 0, 0.12)",
    focusOpacity: 0.12,
    activatedOpacity: 0.12,
  },
};

export const defaultTypography = {
  fontFamily: "Montserrat, sans-serif",
  h1: {
    fontWeight: 700,
    fontSize: "1.25rem",
    lineHeight: 1.3,
    color: defaultPalette.text.primary,
  },
  h2: {
    fontWeight: 600,
    fontSize: "1.125rem",
    lineHeight: 1.2,
    color: defaultPalette.text.primary,
  },
  h3: {
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 1.125,
    color: defaultPalette.text.primary,
  },
  h4: {
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: 1.125,
    color: defaultPalette.text.primary,
  },
  modalLabel: {
    fontWeight: 500,
    fontSize: "0.75rem",
    lineHeight: 1.5,
    color: defaultPalette.themePrimary.tertiary,
  },
  formLabel: {
    fontWeight: 400,
    fontSize: "0.688rem",
    lineHeight: 0.875,
    color: defaultPalette.text.primary,
  },
  propertyLabel: {
    fontWeight: 600,
    fontSize: "0.688rem",
    lineHeight: 0.875,
    color: defaultPalette.text.primary,
  },
  bodyL: {
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: 1.25,
    color: defaultPalette.text.primary,
  },
  bodyM: {
    fontWeight: 400,
    fontSize: "0.938rem",
    lineHeight: 1.125,
    color: defaultPalette.text.primary,
  },
  bodyS: {
    fontWeight: 400,
    fontSize: "0.875rem",
    lineHeight: "1rem",
    color: defaultPalette.text.primary,
  },
  bodyXs: {
    fontWeight: 400,
    fontSize: "0.813rem",
    lineHeight: "18px",
    color: defaultPalette.text.primary,
  },
  body: {
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: "21px",
    color: defaultPalette.text.secondary,
  },
  bodyBold: {
    fontWeight: 700,
    fontSize: "0.875rem",
    lineHeight: "21px",
    color: defaultPalette.text.primary,
  },

  button: {
    fontWeight: 600,
    fontSize: "0.938rem",
    lineHeight: 1,
  },
  caption: {
    fontWeight: 400,
    fontSize: "0.75rem",
    lineHeight: 1,
  },
  overline: {
    fontWeight: 500,
    fontSize: "0.875rem",
    lineHeight: 1,
  },
  input: {
    fontSize: "0.8rem",
  },
  headline: {
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: 1.5,
  },
  recommendationCaption: {
    fontWeight: 500,
    fontSize: "12px",
    lineHeight: 1.5,
  },
};

const defaultShape = {
  borderRadius: 8,
};

const defaultBreakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 900,
    lg: 1024,
    xl: 1200,
    tablet: 1440,
    xxl: 1536,
  },
};

interface CustomThemeOptions extends Omit<ThemeOptions, "palette" | "typography" | "shape" | "breakpoints"> {
  palette?: typeof defaultPalette;
  typography?: typeof defaultTypography;
  shape?: typeof defaultShape;
  breakpoints?: typeof defaultBreakpoints;
}

export function createCustomTheme(options: CustomThemeOptions = {}): Theme {
  const {
    palette = defaultPalette,
    typography = defaultTypography,
    shape = defaultShape,
    breakpoints = defaultBreakpoints,
    ...other
  } = options;

  let theme = createTheme({
    palette,
    typography,
    shape,
    breakpoints,
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          ":root": {
            "--theme-common-blue": palette.common.blue,
            "--theme-common-purple": palette.common.purple,
            "--theme-success-main": palette.success.main,
            "--theme-error-main": palette.error.main,
            "--theme-warning-main": palette.warning.main,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            "&::-webkit-scrollbar": {
              display: "none",
            },
          },
        },
      },
      MuiLoadingButton: {
        styleOverrides: {
          root: {
            borderRadius: "4px",
            textTransform: "capitalize",
            height: "40px",
            minWidth: "100px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap",
          },
          loadingIndicator: {
            color: "white",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "&.MuiTextField-root": {
              border: "none",
            },
            "& .MuiFormLabel-colorError": {
              color: palette.error.main,
            },
            "& .MuiOutlinedInput-root": {
              border: palette.text.hint,
              borderRadius: "4px",
              fontWeight: "400",
              "&.Mui-error": {
                borderColor: palette.error.main,
                fieldset: {
                  borderColor: palette.error.main,
                  color: palette.error.main,
                },

                "&:hover fieldset": {
                  borderColor: palette.error.main,
                },
              },
              "&:hover fieldset": {
                borderColor: "inherit",
              },
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            margin: "0px",
            justifyContent: "flex-start",
          },
          labelPlacementTop: {
            alignItems: "flex-start",
          },
          labelPlacementBottom: {
            alignItems: "flex-start",
          },
          label: {
            fontSize: "11px",
            color: palette.common.black,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: palette.themePrimary.primary,
            padding: "0px 0px 0px 0px",
            borderRadius: "5px",
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            padding: "6px 0px",
            color: palette.themePrimary.primary,
            width: "16px",
            height: "16px",
            marginRight: "10px",
            "&.MuiRadio-colorError": {
              color: palette.error.main,
            },
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            position: "sticky",
            top: -1,
            zIndex: 1,
            borderTop: "0px ",
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            "& .MuiBox-root .MuiTableCell-root.MuiTableCell-sizeMedium.MuiTablePagination-root": {
              borderRadius: "5px",
            },
          },
        },
      },
      MuiTablePagination: {
        styleOverrides: {
          root: {
            borderRadius: "5px",
          },
        },
      },
      MuiIcon: {
        styleOverrides: {
          root: {
            boxSizing: "content-box",
            padding: 3,
            fontSize: "1.125rem",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          root: {
            "& .MuiDialogTitle-root": {
              background: "linear-gradient(90deg, #FFFFFF 0%, #FFDBDB 49.5%, #DCC9FA 100%)",
              color: palette.common.white,
            },
            "& .MuiDialog-container .dialogCoseButton": {
              background: palette.common.white,
              color: palette.common.black,
              width: "40px !important",
              height: "40px !important",
              minWidth: "45px",
              minHeight: "45px",
              borderRadius: "100px ",
              fontWeight: "700",
              position: "absolute",
              top: "0",
              right: "0",
              "&:hover": {
                background: palette.common.white,
                color: palette.common.black,
              },
            },
          },
        },
      },
    },
    ...other,
  });

  theme = responsiveFontSizes(theme);

  return theme;
}
